<template>
  <v-layout
    wrap
    data
  >
    <v-flex
      sm12
      xs12 
      md4
    >
      <v-card
        class="intro-card"
      >
        <v-flex
          class="cover"
          :class="backgroundColor"
          align-self-start
          xs12
        >
          <v-avatar
            class="avatar"
            style="pointer: cursor;"
            :tile="false"
            :size="110"
            :color="$vuetify.theme.dark ? $vuetify.theme.themes.dark.background : $vuetify.theme.themes.light.background"
          >
            <strong
              class="avatar-invert"
              style="font-size:30px;"
            > 
              {{ connectionStats.investor_name | uppercase | substring(0,2) }}
            </strong>
          </v-avatar>
        </v-flex>
          
        <v-flex
          class="description"
          align-self-center
          xs12
        >
          <h4>
            {{ connectionStats.investor_name }}
            <v-tooltip
              v-if="((connectionStats.avg_deposit / connectionStats.init_deposit ) - 1) * 100 >= connectionStats.allowed_drawdown_ptc"
              top
            >
              <template v-slot:activator="{ on }">
                <v-icon
                  class="text-positive"
                  style="margin: -3px 3px 0px;font-size:20px;"
                  v-on="on"
                >
                  mdi-shield-check
                </v-icon>
              </template>
              {{ $t('rankings.investorSave') }}
            </v-tooltip>
          </h4>
          <h5>
            <b>{{ connectionStats.copytrading ? $t('rankings.managementCopyTrading') : $t('rankings.managementApiKey') }}</b>
          </h5>
          <center>
            <v-chip
              class="ma-3"
              color="primary"
              small
              outlined
              pill
            >
              {{ connectionStats.project.ticker }} - {{ connectionStats.project.name }}
            </v-chip>
            <v-img
              class="text-center"
              :src="require(`@/images/${connectionStats.project.exchange}.png`)"
              max-width="100"
              contain
            />
          </center>
        </v-flex> 
        <div
          style="padding:0; position:absolute; bottom:0;width:100%; display:flex;"
        >
          <v-flex xs12>
            <v-btn
              class="bordered round back"
              icon
              text
              small
              @click="$router.go(-1)"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
          </v-flex>
        </div>
      </v-card>
    </v-flex>
    <v-flex
      sm12
      xs12 
      md8
    >
      <v-card
        class="account-overview-card"
        height="100%"
      >
        <v-layout
          wrap
          pa-3
        >
          <v-flex
            md3
            data
            command-line
          >
            <span>{{ $t('globals.status') }}</span>
            <p class="text-neutral">
              <v-icon 
                :class="connectionStats.status =='connected' ? 'blink green-status': 'grey-status'"
              >
                mdi-circle
              </v-icon>
              {{ $t('globals.' + connectionStats.status) }}
            </p>
          </v-flex>
          <v-flex
            md3
            data
          > 
            <span>{{ $t('globals.pnl_total') }}</span>
            <p :class="colorPnL(connectionStats.pnl_total_ptc)">
              {{ connectionStats.pnl_total_ptc }}% 
            </p>
          </v-flex>
          <v-flex
            md3
            data
          > 
            <span>{{ $t('traderdashboard.earnedTotal') }}</span>
            <p :class="colorPnL(connectionStats.management_fee)">
              {{ connectionStats.project.base_currency | renderSymbol }} 
              {{ connectionStats.management_fee | formatMoney }}
            </p>
          </v-flex>
          <v-flex
            md3
            text-right
          >
            <v-menu
              offset-y
              left
              bottom
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :disabled="connectionStats.status != 'connected'"
                  class="mt-2"
                  outlined 
                  text
                  v-on="on"
                >
                  {{ $t('investors.detailsButton') }}
                </v-btn>
              </template>
              <v-sheet dense>
                <v-list-item
                  v-if="!connectionStats.copytrading"
                  @click="viewKeySecret()"
                >
                  <v-list-item-title>{{ $t('accounts.key') }} </v-list-item-title>
                </v-list-item>
                <v-list-item
                  @click="disconnectInvestor()"
                >
                  <v-list-item-title>{{ $t('investors.cancelSub') }}</v-list-item-title>
                </v-list-item>
              </v-sheet>
            </v-menu>
          </v-flex>
        </v-layout>

        <v-divider wrap />

        <v-layout
          wrap
          data
          pa-3
        >
          <v-flex
            md3
            mb-3
          > 
            <span>{{ $t('globals.pnl_this_month') }}</span>
            <p :class="colorPnL(connectionStats.pnl_this_month_ptc)">
              {{ connectionStats.pnl_this_month_ptc }}%
            </p>
          </v-flex>
            
          <v-flex
            v-if="connectionStats.project.strategytype != 'spot'"
            md3
            xs6
            mb-3
          > 
            <span>{{ $t('rankings.upnl') }}</span>
            <p :class="colorPnL(connectionStats.current_upnl_ptc)">
              {{ connectionStats.current_upnl_ptc }}%
            </p>
          </v-flex>
          <v-flex
            v-if="connectionStats.project.strategytype != 'spot'"
            md3
            xs6
            mb-3
          > 
            <span>{{ $t('rankings.trailingStop') }}</span>
            <p class="text-neutral">
              {{ connectionStats.trailing_stop_ptc }}%
            </p>
          </v-flex>
          <v-flex
            v-if="connectionStats.project.strategytype != 'spot'"
            md3
            xs6
            mb-3
          > 
            <span>{{ $t('globals.allowedDrawdown') }}</span>
            <p>
              {{ connectionStats.allowed_drawdown_ptc }}%
            </p>
          </v-flex>
          <v-flex
            md3
            xs6
            mb-3
          > 
            <span>{{ $t('rankings.currentBalance') }}</span>
            <p>
              {{ connectionStats.project.base_currency | renderSymbol }} 
              {{ connectionStats.current_balance | formatMoney }}
            </p>
          </v-flex>
          <v-flex
            md3
            xs6
            mb-3
          > 
            <span>{{ $t('rankings.maxBalance') }}</span>
            <p>
              {{ connectionStats.project.base_currency | renderSymbol }} 
              {{ connectionStats.max_deposit | formatMoney }}
            </p>
          </v-flex>
          <v-flex
            md3
            xs6
            mb-3
          > 
            <span>{{ $t('rankings.initBalance') }}</span>
            <p>
              {{ connectionStats.project.base_currency | renderSymbol }} 
              {{ connectionStats.init_deposit | formatMoney }}
            </p>
          </v-flex>
          <v-flex
            v-if="connectionStats.project.strategytype != 'spot'"
            md3
            xs6
            mb-3
          > 
            <span>{{ $t('rankings.stopBalance') }}</span>
            <p>
              {{ connectionStats.project.base_currency | renderSymbol }} 
              {{ connectionStats.stop_loss_balance | formatMoney }}
            </p>
          </v-flex>
          <v-flex
            v-if="connectionStats.project.strategytype != 'spot'"
            md3
            xs6
            mb-3
          > 
            <span>{{ $t('rankings.lotSize') }}</span>
            <p>
              1:{{ connectionStats.risk_ratio }}
            </p>
          </v-flex>
          <v-flex md3> 
            <span>{{ $t('rankings.dateStart') }}</span>
            <p>
              {{ connectionStats.connection_start | moment("L") }}
            </p>
          </v-flex>
          <v-flex
            md3
            xs6
            mb-3
          > 
            <span>{{ $t('rankings.dateEnd') }}</span>
            <p>
              {{ connectionStats.connection_end == null ? '-' : connectionStats.connection_end | moment("L") }}
            </p>
          </v-flex>
        </v-layout>
      </v-card>
      <disconnect-dialog
        v-if="disconnectDialog"
        :show-dialog="disconnectDialog"
        :connection-id="parseInt(connectionStats.connection_id)"
        @closeDisconnectDialog="closeDisconnectDialog"
      />
      <view-key-secret-dialog
        v-if="keySecretDialog"
        :show-dialog="keySecretDialog"
        :connection-id="parseInt(connectionStats.connection_id)"
      />
    </v-flex>
  </v-layout>
</template>

<script>
import DisconnectDialog from '@/views/Modules/SpotFutures/Connections/partial/DisconnectDialog.vue'
import ViewKeySecretDialog from '@/views/Modules/SpotFutures/Connections/partial/ViewKeySecretDialog.vue'

export default {
  name: 'ConnectionView',
  components: {
    DisconnectDialog,
    ViewKeySecretDialog
  },
  props: {
    connectionStats: {
      type: Object,
      default: null
    },
  },
  data: () => ({
    tab: 0,
    loading: false,
    acceptDialog: false,
    declineDialog: false,
    disconnectDialog: false,
    keySecretDialog: false,
  }),
  computed: {
    backgroundColor() {
      return this.connectionStats.pnl_total_ptc >= 0 ? 'background-positive' : 'background-negative'
    }
  },
  methods: {
    changeTab (i) {
      this.tab = i
    },
    disconnectInvestor() {
      this.disconnectDialog = true
    },
    viewKeySecret() {
      this.keySecretDialog = true
    },
    closeDisconnectDialog(val) {
      this.disconnectDialog = false
      if (val == 'disconnect') {
        this.$parent.$apollo.queries.connectionStats.refetch()
      }
    }
  },
}
</script>



<style lang="scss" >

</style>
